import React, { useEffect, useState, useRef, useContext } from "react";
import { AppContext } from "../../context/AppContext";
import "./FilterSelect.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import SearchInput from "../search/SearchInput";
import { COLORS } from "../../utils/theme";
import { SEARCH_PARAMS } from "../../utils/SearchParams";
import { useCustomSearchParams } from "../hooks/useCustomSearchParams";

const NO_SEARCH = "NO_SEARCH"

const normalizeText = (text) => {
  if (!text) return "";
  return (
    text
      .toLowerCase()
      .trim()
      // Regex removes anything that isn't a letter, number, or space
      .replace(/[^a-z0-9\s]/g, "")
  );
};

const IGNORED_TERMS = new Set([
  "all funders",
  "all publishers",
  "select a funder",
  "select a publisher member",
]);

const DEFAULT_LABELS = ["All funders", "All publishers"];

const FilterSelect = ({
  label = "Filter by",
  icon = "",
  value = "",
  defaultValue = "",
  // Left undefined by consumers that don't have a unique id per option
  // (e.g. TimeFilter) - those keep falling back to label-based matching.
  selectedOptionValue,
  // Only the "Filter by" dropdown (FilterBy.js) has a server-paginated
  // options list, so only it opts into fetching more on scroll.
  enableInfiniteScroll = false,
  options = [],
  onChange,
  width = "half",
  disabled = false,
  showSearchField = false,
  handleIsOpen = () => {},
}) => {
  const [selectedValue, setSelectedValue] = useState(
    value || options?.[0]?.label,
  );
  // Options can share the same display label (e.g. two funders both named
  // "ARC" with different agencyId-based values), so when a unique id is
  // available the "which row is selected" check keys off opt.value instead
  // of opt.label.
  const usesValueBasedSelection = selectedOptionValue !== undefined;
  const [selectedOptValue, setSelectedOptValue] = useState(
    selectedOptionValue || "",
  );
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [searchedValue, setSearchedValue] = useState(null);
  const [searchedIndex, setSearchedIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedItemFromSuggestion, setSelectedItemFromSuggestion] =
    useState(null);
  const wrapperRef = useRef(null);

  // Tracks whether a value was already known when this component first
  // mounted. If it wasn't (value arrives later), that first arrival is a
  // restore from the URL on a real page reload, and gets the one-time
  // auto-populate below regardless of how the filter was originally chosen.
  const skipAutoPopulateSearchRef = useRef(Boolean(value));

  // Once the user has actually touched the search box (typed, backspaced,
  // or picked a search result) in this mount, external re-syncs of `value`
  // (e.g. FilterBy re-deriving the applied selection's label whenever
  // filterByData refreshes - which also happens right after the user
  // clears the box, since clearing triggers an unscoped refetch) must not
  // overwrite what they're typing/have just cleared.
  const hasUserEditedSearchRef = useRef(false);

  const { get } = useCustomSearchParams();

  const {
    entityName,
    filterBySearchParams,
    changeFilterBySearchParams,
    filterByViaSearch,
    filterByHasMore,
    filterByLoadingMore,
    filterByLoading,
    filterByError,
    loadMoreFilterByOptions,
  } = useContext(AppContext);

  // Guards against firing many "load more" calls for a single scroll
  // gesture - the request is async, so filterByLoadingMore (context state)
  // can lag a render or two behind the first scroll event that triggered it.
  const isRequestingMoreRef = useRef(false);

  const handleDropdownScroll = (e) => {
    if (!enableInfiniteScroll) return;
    if (isRequestingMoreRef.current || filterByLoadingMore || !filterByHasMore)
      return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const nearBottom = scrollHeight - (scrollTop + clientHeight) < 60;
    if (!nearBottom) return;

    isRequestingMoreRef.current = true;
    loadMoreFilterByOptions();
    setTimeout(() => {
      isRequestingMoreRef.current = false;
    }, 300);
  };

  // Right after a full page reload, context's filterBySearchParams is reset
  // to null (it's session-only state, not URL-backed) even though a filter
  // is still applied and encoded in the URL's filterByName param. Without
  // this fallback, everything gated on "is a search active" (result
  // sorting, the quick "reset to All X" row) stays off until the user types
  // something new - only the Filter By instance has this URL param.
  const reloadSafeSearchParams =
    filterBySearchParams ||
    (enableInfiniteScroll ? get(SEARCH_PARAMS.FILTER_BY_NAME) : null);

  // A search is only "active" when the user has actually typed something (or
  // it was restored from the URL after a reload) - filterBySearchParams also
  // holds sentinel values (NO_SEARCH / "All funders" / "All publishers") that
  // mean "no filter", not a real search term.
  const isActivelySearching =
    Boolean(reloadSafeSearchParams) &&
    ![NO_SEARCH, "All funders", "All publishers"].includes(
      reloadSafeSearchParams,
    );

  const handleClick = (value) => {
    setIsOpen((prev) => !prev);

    // setIsOpen(value);
    handleIsOpen(value);
  };

  // const handleSearch = (value, updateFilter = true) => {
  //   setSearchedIndex(0);
  //   setSearchedValue(value);
  //   if (updateFilter) {
  //     changeFilterBySearchParams(value);
  //   }

  //   if (!value?.trim()) {
  //     setFilteredOptions(options);
  //     return;
  //   }

  //   const normalizedSearchValue = normalizeText(value);

  //   const filtered = options.filter((item) => {
  //     const rawLabel = item.label.toLowerCase().trim();

  //     if (IGNORED_TERMS.has(rawLabel)) {
  //       return false;
  //     }

  //     const normalizedLabel = normalizeText(item.label);
  //     return normalizedLabel.startsWith(normalizedSearchValue);
  //   });

  //   setFilteredOptions(filtered);
  //   setSelectedItemFromSuggestion(filtered[0]);
  // };

  const handleSearch = (value, updateFilter = true) => {
    hasUserEditedSearchRef.current = true;
    setSearchedIndex(0);
    setSearchedValue(value);
    if (updateFilter) {
      changeFilterBySearchParams(value ? value : NO_SEARCH);
    }

    if (!value?.trim()) {
      setFilteredOptions(options);
      return;
    }

    const normalizedSearchValue = normalizeText(value);

    const filtered = options.filter((item) => {
      const rawLabel = item.label.toLowerCase().trim();

      if (IGNORED_TERMS.has(rawLabel)) {
        return false;
      }

      const normalizedLabel = normalizeText(item.label);
      const normalizedShortName = item.shortName
        ? normalizeText(item.shortName)
        : "";

      // 1. Full label starts with search value
      const labelStarts = normalizedLabel.startsWith(normalizedSearchValue);

      // 2. Any word within the label starts with search value
      const wordStarts = normalizedLabel
        .split(" ")
        .some((word) => word.startsWith(normalizedSearchValue));

      // 3. Short name starts with search value
      const shortNameStarts = normalizedShortName.startsWith(
        normalizedSearchValue,
      );

      return labelStarts || wordStarts || shortNameStarts;
    });

    setFilteredOptions(filtered);
    setSelectedItemFromSuggestion(filtered[0]);
  };

  // useEffect(() => {
  //   if (options) {
  //     setFilteredOptions(options);
  //   }
  // }, [options]);

  //FUTURE CODE
  useEffect(() => {
    if (options) {
      if (isActivelySearching) {
        // While "Filter by" data is being (re)fetched for this search term,
        // DashboardLayout transiently resets filterByData down to just the
        // default placeholder option. Recomputing suggestions against that
        // empty snapshot would look like "no matches" and wipe out the
        // ghost-text suggestion handleSearch just set locally, so wait for
        // the authoritative server results before touching it.
        if (filterByLoading) return;

        const filtered = options.filter((item) => {
          if (!item || item.label === undefined || item.label === null) {
            return false;
          }
          const rawLabel = String(item.label).toLowerCase().trim();
          return !IGNORED_TERMS.has(rawLabel);
        });

        setFilteredOptions(filtered);
        setSelectedItemFromSuggestion(filtered[0]);
      } else {
      setFilteredOptions(options);
      }
    }
  }, [
    options,
    filterByLoading,
    isActivelySearching,
    reloadSafeSearchParams,
  ]);

  useEffect(() => {
    if (!usesValueBasedSelection) return;
    setSelectedOptValue(selectedOptionValue || "");
  }, [selectedOptionValue, usesValueBasedSelection]);

  useEffect(() => {
    if (!value) return;

    setSelectedValue(value);

    // First arrival of a value after mount means it was just restored
    // from the URL on a page reload - populate regardless of how it was
    // originally selected. Consume this one-time opportunity either way.
    const isReloadRestore = !skipAutoPopulateSearchRef.current;
    skipAutoPopulateSearchRef.current = true;

    if (DEFAULT_LABELS.includes(value) && !isActivelySearching) {
      // Resetting to the default option must clear the search box, however
      // the reset was triggered (dropdown click, or externally e.g. the
      // breadcrumb in HeaderNavigate resetting the filter directly). But
      // guard on !isActivelySearching: while the user is mid-search, a
      // default label can be transiently pushed into `value` (FilterBy
      // re-deriving from the still-default selection during the scoped
      // refetch), and that must NOT wipe what they're typing.
      setSearchedValue(null);
    } else if (
      !hasUserEditedSearchRef.current &&
      (filterByViaSearch || isReloadRestore)
    ) {
      // Once the user has touched the search box themselves, don't let a
      // `value` echo (e.g. FilterBy re-deriving the applied selection's
      // label after every filterByData refresh, including the refetch
      // triggered by the user just clearing this box) type over them.
      setSearchedValue(value);
    }
  }, [value, filterByViaSearch, isActivelySearching]);

  /* outside click */
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  // style={{ width }}
  return (
    <div className="filter-by-container container  ms-0">
      <div className="row gy-3" ref={wrapperRef}>
        {label && (
          <label className="col-12 col-sm-2 col-lg-1 fs-5 ps-0  pe-1 pe-xl-0">
            {label}
          </label>
        )}

        <div
          id="filter-select_dropdown_id"
          className={`dropdown filter-by-dropdown col-12  px-0 ${width === "full" ? "col-sm-12" : "col-sm-6 col-lg-4"}`}
        >
          <button
            className="btn w-100 d-flex justify-content-between align-items-center border rounded px-3 py-2"
            type="button"
            data-bs-toggle="dropdown"
            // data-bs-auto-close="inside"
            aria-expanded="false"
            onClick={() => handleClick(selectedValue)}
            disabled={disabled}
          >
            <span className="me-2 text-start">
              {icon && (
                <FontAwesomeIcon
                  icon={icon}
                  color={COLORS.THEME_COLOR}
                  className="pe-1"
                />
              )}
              <span className="text-muted">
                {selectedValue?.toString().trim()}
              </span>
            </span>
            {isOpen ? (
              <FontAwesomeIcon icon={faChevronUp} color={COLORS.THEME_COLOR} />
            ) : (
              <FontAwesomeIcon
                icon={faChevronDown}
                color={COLORS.THEME_COLOR}
              />
            )}
          </button>

          <ul
            className="dropdown-menu w-100  dropdown-scroll py-0"
            onScroll={handleDropdownScroll}
          >
            {options?.length > 0 && showSearchField && (
              <li className="dropdown-item  text-muted">
                <div className="position-relative" style={{ width: "100%" }}>
                  <div
                    className="d-flex autocomplete-suggestion_filter_by text-truncate"
                    style={{ width: "90%" }}
                  >
                    {searchedValue}
                    <div className="ghost-text text-truncate">
                      {selectedItemFromSuggestion &&
                      searchedValue &&
                      selectedItemFromSuggestion.label
                        .trim()
                        .toLowerCase()
                        .startsWith(searchedValue.toLowerCase())
                        ? selectedItemFromSuggestion.label
                            .trim()
                            .slice(searchedValue.length)
                        : ""}
                    </div>
                  </div>
                  <input
                    type="search"
                    placeholder={`Search`}
                    // placeholder={`Search for a ${entityName}`}
                    className="w-100 border px-2 autocomplete-input_filter_by"
                    value={searchedValue?.trimStart() || ""}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleSearch(value, true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        const nextIndex =
                          searchedIndex < filteredOptions.length - 1
                            ? searchedIndex + 1
                            : 0;

                        setSearchedIndex(nextIndex);
                        setSelectedItemFromSuggestion(
                          filteredOptions[nextIndex],
                        );
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        const prevIndex =
                          searchedIndex > 0 ? searchedIndex - 1 : 0;
                        setSearchedIndex(prevIndex);
                        setSelectedItemFromSuggestion(
                          filteredOptions[prevIndex],
                        );
                      } else if (e.key === "Enter") {
                        e.preventDefault();

                        const opt = filteredOptions?.[searchedIndex];

                        if (!opt) {
                          return;
                        }

                        onChange(opt, true);
                        setSelectedValue(opt.label?.trim());
                        setSelectedOptValue(opt.value);
                        setSearchedValue(opt.label?.trim());
                        setSearchedIndex(0);
                        handleSearch(opt.label?.trim(), true);
                        handleClick(opt.value);
                        document
                          .getElementById("filter-select_dropdown_id")
                          ?.click();
                        // e.target.blur(); // close dropdown
                      }
                    }}
                  />
                </div>
              </li>
            )}

            {isActivelySearching &&
              selectedValue &&
              !["All publishers", "All funders"].includes(selectedValue) && (
                <li
                  className="dropdown-item dropdown-item-custom d-flex justify-content-between px-4"
                  onClick={() => {
                    const opt = options[0];

                    if (!opt) {
                      return;
                    }
                    onChange(opt, false);
                    setSelectedValue(opt.label?.toString().trim());
                    setSelectedOptValue(opt.value);
                    handleSearch("");
                    handleClick(opt?.value);
                  }}
                >
                  {options[0]?.label}
                  {/* Reset to {options[0]?.label.toLowerCase()} */}
                </li>
              )}

            {filteredOptions.map((opt, index) => {
              return (
                <li
                  key={index}
                  className={`dropdown-item dropdown-item-custom  ${isFocused && index === searchedIndex && isActivelySearching ? "bg-theme text-light" : "text-muted"} ${
                    (
                      usesValueBasedSelection
                        ? selectedOptValue && String(selectedOptValue) === String(opt?.value)
                        : String(selectedValue).trim() === String(opt?.label).trim()
                    )
                      ? "active-item"
                      : ""
                  } ${opt.disabled ? "disabled-item" : ""}`}
                  value={opt.label}
                  disabled={opt.disabled}
                  title={opt.label}
                  onClick={(e) => {
                    if (opt.disabled) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    onChange(opt, isActivelySearching);
                    // onChange(opt.value);
                    setSelectedValue(opt.label?.toString().trim());
                    setSelectedOptValue(opt.value);
                    if (DEFAULT_LABELS.includes(opt.label?.toString().trim())) {
                      // Resetting to the default option must always clear the
                      // search box, even if it was populated by the one-time
                      // reload-restore rather than a live search (in which
                      // case isActivelySearching is still false here).
                      setSearchedValue(null);
                    } else if (isActivelySearching) {
                      setSearchedValue(opt.label?.toString().trim());
                      handleSearch(opt.label?.trim(), true);
                    } else {
                      // Plain click on the prepopulated list (no active
                      // search) - explicitly record "no search" so a stale
                      // filterByName left in the URL from an earlier search
                      // doesn't get picked up via reloadSafeSearchParams as
                      // the active search term the next time this dropdown
                      // reopens, which would wrongly re-trigger the sort.
                      changeFilterBySearchParams(NO_SEARCH);
                    }
                    handleClick(opt.value);
                  }}
                >
                  {opt.label}
                </li>
              );
            })}

            {isActivelySearching &&
              !filterByLoading &&
              filteredOptions.length === 0 && (
                <li className="dropdown-item text-muted text-center">
                  No data found
                </li>
              )}

            {enableInfiniteScroll && filterByLoading && (
              <li className="dropdown-item text-muted text-center">
                Loading...
              </li>
            )}

            {enableInfiniteScroll && filterByLoadingMore && (
              <li className="dropdown-item text-muted text-center">
                Loading more...
              </li>
            )}

            {enableInfiniteScroll && filterByError && (
              <li className="dropdown-item text-danger text-center small py-2">
                {filterByError}
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FilterSelect;
